import { useState } from "react";
import { Link } from "react-router-dom";
import { aboutInfo } from "@/mocks/content";

function ContactForm() {
  const [submitStatus, setSubmitStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("website_alt") as string;
    if (honeypot?.trim()) {
      setSubmitStatus("success");
      form.reset();
      setTimeout(() => setSubmitStatus("idle"), 3000);
      return;
    }

    setSubmitStatus("loading");
    setErrorMessage("");

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== "website_alt" && typeof value === "string" && value.trim()) {
          params.append(key, value.trim());
        }
      });

      const response = await fetch(form.action, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {}

      if (response.ok && parsed?.code === "OK") {
        setSubmitStatus("success");
        form.reset();
        setTimeout(() => setSubmitStatus("idle"), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "提交失败，请稍后再试";
        setErrorMessage(serverMsg);
        setSubmitStatus("error");
      }
    } catch {
      setErrorMessage("网络错误，请检查网络后重试");
      setSubmitStatus("error");
    }
  };

  return (
    <form className="space-y-3" data-readdy-form="true" action="https://readdy.ai/api/form/d9opqhgsenbniolesjig" method="POST" onSubmit={handleSubmit}>
      <div>
        <input
          type="text"
          name="name"
          placeholder="您的姓名"
          className="w-full px-4 py-2.5 rounded-lg border border-background-200/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
          required
        />
      </div>
      <div>
        <input
          type="email"
          name="email"
          placeholder="您的邮箱"
          className="w-full px-4 py-2.5 rounded-lg border border-background-200/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
          required
        />
      </div>
      <div>
        <textarea
          name="message"
          placeholder="请输入您的消息（最多500字）"
          rows={4}
          maxLength={500}
          className="w-full px-4 py-2.5 rounded-lg border border-background-200/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
          required
        ></textarea>
      </div>
      <div className="hp-field">
        <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
      </div>

      {submitStatus === "success" && (
        <div className="bg-accent-100 text-accent-800 text-sm px-4 py-2.5 rounded-lg flex items-center gap-2">
          <i className="ri-checkbox-circle-line"></i> 消息已发送，我们会尽快回复您！
        </div>
      )}

      {submitStatus === "error" && (
        <div className="bg-primary-100 text-primary-700 text-sm px-4 py-2.5 rounded-lg flex items-center gap-2">
          <i className="ri-error-warning-line"></i> {errorMessage || "提交失败，请稍后再试"}
        </div>
      )}

      <button
        type="submit"
        disabled={submitStatus === "loading"}
        className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-lg text-sm transition-colors whitespace-nowrap flex items-center justify-center gap-2"
      >
        {submitStatus === "loading" ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i> 发送中...
          </>
        ) : (
          "发送消息"
        )}
      </button>
    </form>
  );
}

export default function AboutPage() {
  return (
    <main className="pt-16 bg-background-50">
      <div className="bg-background-100 border-b border-background-200/60">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
          <nav className="flex items-center gap-1 text-sm text-foreground-500 mb-4 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-900 font-medium">关于我们</span>
          </nav>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">关于91吃瓜</h1>
        </div>
      </div>

      <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
        <div className="max-w-[1280px] mx-auto flex flex-col lg:flex-row gap-10 items-center">
          <div className="lg:w-1/2">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">品牌介绍</h2>
            <p className="text-foreground-700 leading-relaxed mb-4">{aboutInfo.brandIntro}</p>
            <p className="text-foreground-700 leading-relaxed font-medium text-primary-700">{aboutInfo.mission}</p>
          </div>
          <div className="lg:w-1/2 grid grid-cols-2 gap-4 w-full">
            {aboutInfo.stats.map((stat) => (
              <div key={stat.label} className="bg-background-100 rounded-xl p-5 text-center border border-background-200/60">
                <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">{stat.value}</div>
                <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
        <div className="max-w-[1280px] mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-2">我们的价值观</h2>
            <p className="text-sm text-foreground-500">坚守初心，为用户提供最优质的吃瓜内容体验</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {aboutInfo.values.map((val) => (
              <div key={val.title} className="bg-background-50 rounded-xl p-5 border border-background-200/60">
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">{val.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{val.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
        <div className="max-w-[1280px] mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-2">发展历程</h2>
            <p className="text-sm text-foreground-500">从2020年至今，我们一路成长</p>
          </div>
          <div className="relative max-w-2xl mx-auto">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
            {aboutInfo.timeline.map((item, idx) => (
              <div key={item.year} className={`relative flex items-start gap-6 mb-8 ${idx % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}>
                <div className={`flex-1 ${idx % 2 === 0 ? "md:text-right" : "md:text-left"} pl-10 md:pl-0`}>
                  <div className="bg-background-100 rounded-xl p-4 border border-background-200/60 inline-block">
                    <span className="text-sm font-bold text-primary-500">{item.year}</span>
                    <p className="text-sm text-foreground-700 mt-1 leading-relaxed">{item.event}</p>
                  </div>
                </div>
                <div className="absolute left-0 md:left-1/2 w-8 h-8 flex items-center justify-center -translate-x-1/2 bg-accent-500 rounded-full z-10">
                  <i className="ri-check-line text-white text-sm"></i>
                </div>
                <div className="flex-1 hidden md:block"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
        <div className="max-w-[1280px] mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-2">常见问题</h2>
            <p className="text-sm text-foreground-500">关于91吃瓜，您想了解的都在这</p>
          </div>
          <div className="max-w-2xl mx-auto space-y-3">
            {aboutInfo.faqs.map((faq, idx) => (
              <details key={idx} className="group bg-background-50 rounded-xl border border-background-200/60 overflow-hidden">
                <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
                  <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                  <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                  <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                </summary>
                <div className="px-4 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50" id="contact">
        <div className="max-w-[1280px] mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-2">联系我们</h2>
            <p className="text-sm text-foreground-500">如有任何问题或建议，欢迎随时联系我们</p>
          </div>
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-background-100 rounded-xl border border-background-200/60">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <div>
                  <h4 className="text-sm font-medium text-foreground-900">电子邮箱</h4>
                  <p className="text-sm text-foreground-600">{aboutInfo.contact.email}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-background-100 rounded-xl border border-background-200/60">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-phone-line text-lg"></i>
                </span>
                <div>
                  <h4 className="text-sm font-medium text-foreground-900">客服电话</h4>
                  <p className="text-sm text-foreground-600">{aboutInfo.contact.phone}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-background-100 rounded-xl border border-background-200/60">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-map-pin-line text-lg"></i>
                </span>
                <div>
                  <h4 className="text-sm font-medium text-foreground-900">公司地址</h4>
                  <p className="text-sm text-foreground-600">{aboutInfo.contact.address}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-background-100 rounded-xl border border-background-200/60">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-wechat-line text-lg"></i>
                </span>
                <div>
                  <h4 className="text-sm font-medium text-foreground-900">微信公众号</h4>
                  <p className="text-sm text-foreground-600">{aboutInfo.contact.wechat}</p>
                </div>
              </div>
            </div>

            <div className="bg-background-100 rounded-xl border border-background-200/60 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-900 mb-4">发送消息</h3>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "关于91吃瓜",
            "description": aboutInfo.brandIntro,
            "url": "https://hnxyljy.cn/about",
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": aboutInfo.faqs.map((faq) => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer,
              },
            })),
          }),
        }}
      />
    </main>
  );
}